import { useEffect, useRef, useState } from 'react';

/* PdfThumbnail — renders ONE page of a PDF as an inline image using
 * pdf.js. Used by MediaTile (single-page first-frame) and by PdfViewer
 * (page-by-page swipe).
 *
 * Cache key includes the page index so flipping pages re-uses each
 * page's pixels on revisit. IN_FLIGHT de-dupes concurrent requests.
 *
 * Props:
 *   url        – PDF URL (absolute)
 *   pageIndex  – 1-based page number (default 1)
 *   alt        – alt text
 *   className  – tailwind classes for the <img>
 *   style      – inline styles for the <img>
 *   onFail     – callback when pdf.js can't load the file
 *   onMeta     – callback({numPages}) once the document is parsed —
 *                lets PdfViewer learn the page count without a 2nd fetch
 */

const CACHE = new Map();      // `${url}#${pageIndex}` -> JPEG dataURL
const META_CACHE = new Map(); // url                   -> { numPages }
const IN_FLIGHT = new Map();  // same key as CACHE     -> Promise
let _pdfjsReady = null;

async function getPdfjs() {
  if (_pdfjsReady) return _pdfjsReady;
  _pdfjsReady = (async () => {
    const lib = await import('pdfjs-dist/legacy/build/pdf');
    lib.GlobalWorkerOptions.workerSrc =
      `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${lib.version}/pdf.worker.min.mjs`;
    return lib;
  })();
  return _pdfjsReady;
}

// Document cache (so flipping pages doesn't re-fetch + re-parse the PDF).
const DOC_CACHE = new Map(); // url -> pdf.js PDFDocumentProxy
const DOC_IN_FLIGHT = new Map();

async function loadDoc(url) {
  if (DOC_CACHE.has(url)) return DOC_CACHE.get(url);
  if (DOC_IN_FLIGHT.has(url)) return DOC_IN_FLIGHT.get(url);
  const promise = (async () => {
    const pdfjsLib = await getPdfjs();
    const loadingTask = pdfjsLib.getDocument({ url, disableFontFace: true, verbosity: 0 });
    const pdf = await loadingTask.promise;
    DOC_CACHE.set(url, pdf);
    META_CACHE.set(url, { numPages: pdf.numPages });
    return pdf;
  })();
  DOC_IN_FLIGHT.set(url, promise);
  try {
    return await promise;
  } finally {
    DOC_IN_FLIGHT.delete(url);
  }
}

async function renderPage(url, pageIndex) {
  const cacheKey = `${url}#${pageIndex}`;
  if (CACHE.has(cacheKey)) {
    return { dataUrl: CACHE.get(cacheKey), numPages: META_CACHE.get(url)?.numPages || 1 };
  }
  if (IN_FLIGHT.has(cacheKey)) return IN_FLIGHT.get(cacheKey);
  const work = (async () => {
    const pdf = await loadDoc(url);
    const safeIdx = Math.max(1, Math.min(pdf.numPages, pageIndex));
    const page = await pdf.getPage(safeIdx);
    const viewport = page.getViewport({ scale: 1.5 });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = Math.min(800, Math.ceil(viewport.width));
    canvas.height = Math.ceil((canvas.width / viewport.width) * viewport.height);
    const scaledViewport = page.getViewport({ scale: (canvas.width / viewport.width) * 1.5 });
    await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    CACHE.set(cacheKey, dataUrl);
    return { dataUrl, numPages: pdf.numPages };
  })();
  IN_FLIGHT.set(cacheKey, work);
  try {
    return await work;
  } finally {
    IN_FLIGHT.delete(cacheKey);
  }
}

export default function PdfThumbnail({ url, pageIndex = 1, alt = 'PDF preview', className = '', style = {}, onFail, onMeta }) {
  const cacheKey = `${url}#${pageIndex}`;
  const [dataUrl, setDataUrl] = useState(() => CACHE.get(cacheKey) || null);
  const mountedRef = useRef(true);

  // Fire onMeta from cache synchronously when possible so PdfViewer can
  // size its arrow controls before the next render.
  useEffect(() => {
    if (META_CACHE.has(url) && onMeta) {
      onMeta(META_CACHE.get(url));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    mountedRef.current = true;
    if (CACHE.has(cacheKey)) {
      setDataUrl(CACHE.get(cacheKey));
      return;
    }
    setDataUrl(null);
    renderPage(url, pageIndex)
      .then(({ dataUrl: src, numPages }) => {
        if (!mountedRef.current) return;
        setDataUrl(src);
        if (onMeta) onMeta({ numPages });
      })
      .catch(() => {
        if (mountedRef.current && onFail) onFail();
      });
    return () => { mountedRef.current = false; };
    // cacheKey already encodes both url + pageIndex
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (!dataUrl) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ backgroundColor: '#F5F0E1', ...style }}>
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#DC2626', borderTopColor: 'transparent' }} />
      </div>
    );
  }
  return (
    <img
      data-testid="pdf-thumb"
      src={dataUrl}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
    />
  );
}
